import { useEffect, useRef } from "react";
import "./dna-entry-transition.css";

export const DNA_ENTRY_DURATION_MS = 2500;

const REDUCED_MOTION_DURATION_MS = 360;
const ROLL_START_MS = 600;
const ROLL_END_MS = 1900;
const SAMPLE_COUNT = 42;
const RUNG_COUNT = 13;
const TAU = Math.PI * 2;
const INITIAL_PHASE = Math.PI * 0.16;
const ROLL_RADIANS = Math.PI * 3;
const HELIX_TURNS = 2.18;
const VIEWBOX_WIDTH = 1000;
const AXIS_START = 72;
const AXIS_END = VIEWBOX_WIDTH - AXIS_START;
const AXIS_Y = 160;
const HELIX_RADIUS = 83;

const DEPTH_BANDS = ["back", "middle", "front"] as const;

type DepthBand = (typeof DEPTH_BANDS)[number];

interface Point {
  x: number;
  y: number;
  z: number;
}

interface RungSegment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  opacity: number;
  width: number;
}

interface RungGeometry {
  back: RungSegment;
  front: RungSegment;
}

interface DnaGeometry {
  strands: [string[], string[]];
  pulse: string;
  rungs: RungGeometry[];
}

interface DnaEntryTransitionProps {
  onComplete: () => void;
}

function clamp(value: number, minimum = 0, maximum = 1) {
  return Math.min(maximum, Math.max(minimum, value));
}

function smootherStep(value: number) {
  const t = clamp(value);
  return t * t * t * (t * (t * 6 - 15) + 10);
}

function format(value: number) {
  return value.toFixed(2);
}

function pointAt(progress: number, phase: number, strand: 0 | 1): Point {
  const theta =
    progress * HELIX_TURNS * TAU + phase + (strand === 1 ? Math.PI : 0);
  const z = Math.cos(theta);
  const perspective = 1 + z * 0.09;

  return {
    x: AXIS_START + progress * (AXIS_END - AXIS_START),
    y: AXIS_Y + Math.sin(theta) * HELIX_RADIUS * perspective,
    z,
  };
}

function segmentBand(depth: number): 0 | 1 | 2 {
  if (depth < -0.34) {
    return 0;
  }
  if (depth < 0.34) {
    return 1;
  }
  return 2;
}

function pointCommand(point: Point) {
  return `${format(point.x)} ${format(point.y)}`;
}

function createGeometry(phase: number): DnaGeometry {
  const strands: [string[], string[]] = [
    ["", "", ""],
    ["", "", ""],
  ];
  const strandPoints: [Point[], Point[]] = [[], []];

  for (let strand = 0 as 0 | 1; strand <= 1; strand = (strand + 1) as 0 | 1) {
    const points = strandPoints[strand];

    for (let index = 0; index <= SAMPLE_COUNT; index += 1) {
      points.push(pointAt(index / SAMPLE_COUNT, phase, strand));
    }

    for (let index = 1; index < points.length; index += 1) {
      const previous = points[index - 1];
      const current = points[index];
      const band = segmentBand((previous.z + current.z) / 2);
      strands[strand][band] +=
        `M ${pointCommand(previous)} L ${pointCommand(current)} `;
    }
  }

  const pulse =
    `M ${pointCommand(strandPoints[0][0])} ` +
    strandPoints[0]
      .slice(1)
      .map((point) => `L ${pointCommand(point)}`)
      .join(" ");

  const rungs = Array.from({ length: RUNG_COUNT }, (_, index) => {
    const progress = (index + 1) / (RUNG_COUNT + 1);
    const first = pointAt(progress, phase, 0);
    const second = pointAt(progress, phase, 1);
    const far = first.z < second.z ? first : second;
    const near = first.z < second.z ? second : first;
    const middle = {
      x: (first.x + second.x) / 2,
      y: (first.y + second.y) / 2,
    };
    const projectedSpan = clamp(
      Math.abs(first.y - second.y) / (HELIX_RADIUS * 2),
    );

    return {
      back: {
        x1: far.x,
        y1: far.y,
        x2: middle.x,
        y2: middle.y,
        opacity: 0.26 + projectedSpan * 0.34,
        width: 2 + projectedSpan * 1,
      },
      front: {
        x1: middle.x,
        y1: middle.y,
        x2: near.x,
        y2: near.y,
        opacity: 0.46 + projectedSpan * 0.42,
        width: 2.3 + projectedSpan * 1.3,
      },
    };
  });

  return { strands, pulse, rungs };
}

const INITIAL_GEOMETRY = createGeometry(INITIAL_PHASE);

function setRungGeometry(
  element: SVGLineElement | null,
  segment: RungSegment,
) {
  if (!element) {
    return;
  }

  element.setAttribute("x1", format(segment.x1));
  element.setAttribute("y1", format(segment.y1));
  element.setAttribute("x2", format(segment.x2));
  element.setAttribute("y2", format(segment.y2));
  element.setAttribute("opacity", format(segment.opacity));
  element.setAttribute("stroke-width", format(segment.width));
}

export function DnaEntryTransition({
  onComplete,
}: DnaEntryTransitionProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const visualRef = useRef<HTMLDivElement>(null);
  const pulseRef = useRef<SVGPathElement>(null);
  const strandRefs = useRef<Array<SVGPathElement | null>>([]);
  const backRungRefs = useRef<Array<SVGLineElement | null>>([]);
  const frontRungRefs = useRef<Array<SVGLineElement | null>>([]);
  const onCompleteRef = useRef(onComplete);
  const completedRef = useRef(false);
  const finishRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    const root = rootRef.current;
    const visual = visualRef.current;

    if (!root || !visual) {
      return;
    }

    let timerId: number | undefined;
    let frameId: number | undefined;
    let disposed = false;
    const animations: Animation[] = [];
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const duration = reducedMotion
      ? REDUCED_MOTION_DURATION_MS
      : DNA_ENTRY_DURATION_MS;

    const cancelRuntime = () => {
      if (timerId !== undefined) {
        window.clearTimeout(timerId);
        timerId = undefined;
      }
      if (frameId !== undefined) {
        window.cancelAnimationFrame(frameId);
        frameId = undefined;
      }
      animations.forEach((animation) => animation.cancel());
      animations.length = 0;
      window.removeEventListener("keydown", handleKeyDown);
      root.classList.remove("dna-entry-transition--active");
    };

    const finish = () => {
      if (completedRef.current || disposed) {
        return;
      }

      completedRef.current = true;
      root.style.opacity = "0";
      cancelRuntime();
      onCompleteRef.current();
    };

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        finish();
      }
    }

    const animateSafely = (
      element: Element,
      keyframes: Keyframe[],
      options: KeyframeAnimationOptions,
    ) => {
      try {
        animations.push(element.animate(keyframes, options));
      } catch {
        // The static SVG is the intentional fallback when WAAPI is unavailable.
      }
    };

    root.classList.add("dna-entry-transition--active");
    finishRef.current = finish;
    window.addEventListener("keydown", handleKeyDown);

    if (reducedMotion) {
      animateSafely(
        root,
        [
          { opacity: 1, offset: 0 },
          { opacity: 1, offset: 0.62 },
          { opacity: 0, offset: 1 },
        ],
        { duration, easing: "linear", fill: "forwards" },
      );
      animateSafely(
        visual,
        [
          { filter: "blur(2px)", opacity: 0.72, transform: "scale(0.985)" },
          { filter: "blur(0)", opacity: 1, transform: "scale(1)", offset: 0.42 },
          {
            filter: "blur(1px)",
            opacity: 0,
            transform: "scale(1.018)",
            offset: 1,
          },
        ],
        { duration, easing: "linear", fill: "forwards" },
      );
    } else {
      animateSafely(
        root,
        [
          { opacity: 1, offset: 0 },
          { opacity: 1, offset: 0.76 },
          { opacity: 0.25, offset: 0.88 },
          { opacity: 0, offset: 0.92 },
          { opacity: 0, offset: 1 },
        ],
        {
          duration,
          easing: "linear",
          fill: "forwards",
        },
      );
      animateSafely(
        visual,
        [
          {
            filter: "blur(9px)",
            opacity: 0.28,
            transform: "scale(0.9)",
            offset: 0,
          },
          {
            filter: "blur(2px)",
            opacity: 0.82,
            transform: "scale(0.97)",
            offset: 0.12,
          },
          {
            filter: "blur(0)",
            opacity: 1,
            transform: "scale(1)",
            offset: 0.24,
          },
          {
            filter: "blur(0)",
            opacity: 1,
            transform: "scale(1)",
            offset: 0.76,
          },
          {
            filter: "blur(3px)",
            opacity: 0.18,
            transform: "scale(1.08)",
            offset: 0.88,
          },
          {
            filter: "blur(6px)",
            opacity: 0,
            transform: "scale(1.12)",
            offset: 0.92,
          },
          {
            filter: "blur(6px)",
            opacity: 0,
            transform: "scale(1.12)",
            offset: 1,
          },
        ],
        { duration, easing: "linear", fill: "forwards" },
      );

      if (pulseRef.current) {
        animateSafely(
          pulseRef.current,
          [
            { opacity: 0, strokeDashoffset: "0.82", offset: 0 },
            { opacity: 0, strokeDashoffset: "0.82", offset: 0.36 },
            { opacity: 0.96, strokeDashoffset: "0.63", offset: 0.44 },
            { opacity: 0.82, strokeDashoffset: "0.29", offset: 0.52 },
            { opacity: 0, strokeDashoffset: "0.08", offset: 0.6 },
            { opacity: 0, strokeDashoffset: "0.08", offset: 1 },
          ],
          { duration, easing: "linear", fill: "forwards" },
        );
      }

      const startedAt = performance.now();
      const drawFrame = (now: number) => {
        const elapsed = now - startedAt;
        const rollProgress = smootherStep(
          (elapsed - ROLL_START_MS) / (ROLL_END_MS - ROLL_START_MS),
        );
        const geometry = createGeometry(
          INITIAL_PHASE + rollProgress * ROLL_RADIANS,
        );

        for (let strand = 0; strand < 2; strand += 1) {
          for (let band = 0; band < DEPTH_BANDS.length; band += 1) {
            strandRefs.current[strand * DEPTH_BANDS.length + band]?.setAttribute(
              "d",
              geometry.strands[strand][band],
            );
          }
        }

        pulseRef.current?.setAttribute("d", geometry.pulse);
        geometry.rungs.forEach((rung, index) => {
          setRungGeometry(backRungRefs.current[index], rung.back);
          setRungGeometry(frontRungRefs.current[index], rung.front);
        });

        if (elapsed < DNA_ENTRY_DURATION_MS && !completedRef.current) {
          frameId = window.requestAnimationFrame(drawFrame);
        }
      };

      frameId = window.requestAnimationFrame(drawFrame);
    }

    timerId = window.setTimeout(finish, duration);

    return () => {
      disposed = true;
      finishRef.current = () => undefined;
      cancelRuntime();
    };
  }, []);

  return (
    <div
      className="dna-entry-transition"
      data-testid="dna-entry-transition"
      ref={rootRef}
    >
      <p
        className="dna-entry-transition__status"
        role="status"
        aria-live="polite"
      >
        正在进入研究工作台
      </p>

      <div
        className="dna-entry-transition__visual"
        ref={visualRef}
        aria-hidden="true"
      >
        <svg
          className="dna-entry-transition__svg"
          viewBox="0 0 1000 320"
          focusable="false"
        >
          <defs>
            <linearGradient
              id="dna-entry-teal-back"
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="0" stopColor="#78978F" />
              <stop offset="0.55" stopColor="#A9BEB8" />
              <stop offset="1" stopColor="#6F8F86" />
            </linearGradient>
            <linearGradient
              id="dna-entry-teal-middle"
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="0" stopColor="#0B5F59" />
              <stop offset="0.48" stopColor="#3C9A80" />
              <stop offset="1" stopColor="#0F766E" />
            </linearGradient>
            <linearGradient
              id="dna-entry-teal-front"
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="0" stopColor="#0F766E" />
              <stop offset="0.38" stopColor="#86DDBD" />
              <stop offset="0.58" stopColor="#D7F4E8" />
              <stop offset="1" stopColor="#237A57" />
            </linearGradient>
            <filter
              id="dna-entry-green-glow"
              x="-30%"
              y="-90%"
              width="160%"
              height="280%"
              colorInterpolationFilters="sRGB"
            >
              <feGaussianBlur stdDeviation="3.5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          <g className="dna-entry-transition__strand-layer">
            {([0, 1] as const).map((strand) => (
              <path
                className="dna-entry-transition__strand dna-entry-transition__strand--back"
                d={INITIAL_GEOMETRY.strands[strand][0]}
                key={`back-${strand}`}
                ref={(element) => {
                  strandRefs.current[strand * DEPTH_BANDS.length] = element;
                }}
              />
            ))}
          </g>

          <g className="dna-entry-transition__rungs dna-entry-transition__rungs--back">
            {INITIAL_GEOMETRY.rungs.map((rung, index) => (
              <line
                key={`back-${index}`}
                x1={rung.back.x1}
                y1={rung.back.y1}
                x2={rung.back.x2}
                y2={rung.back.y2}
                opacity={rung.back.opacity}
                strokeWidth={rung.back.width}
                ref={(element) => {
                  backRungRefs.current[index] = element;
                }}
              />
            ))}
          </g>

          <g className="dna-entry-transition__rungs dna-entry-transition__rungs--front">
            {INITIAL_GEOMETRY.rungs.map((rung, index) => (
              <line
                key={`front-${index}`}
                x1={rung.front.x1}
                y1={rung.front.y1}
                x2={rung.front.x2}
                y2={rung.front.y2}
                opacity={rung.front.opacity}
                strokeWidth={rung.front.width}
                ref={(element) => {
                  frontRungRefs.current[index] = element;
                }}
              />
            ))}
          </g>

          <g className="dna-entry-transition__strand-layer">
            {([1, 2] as const).map((bandIndex) =>
              ([0, 1] as const).map((strand) => (
                <path
                  className={`dna-entry-transition__strand dna-entry-transition__strand--${DEPTH_BANDS[bandIndex]}`}
                  d={INITIAL_GEOMETRY.strands[strand][bandIndex]}
                  key={`${DEPTH_BANDS[bandIndex]}-${strand}`}
                  ref={(element) => {
                    strandRefs.current[
                      strand * DEPTH_BANDS.length + bandIndex
                    ] = element;
                  }}
                />
              )),
            )}
          </g>

          <path
            className="dna-entry-transition__pulse"
            d={INITIAL_GEOMETRY.pulse}
            pathLength="1"
            ref={pulseRef}
          />
        </svg>
      </div>

      <button
        className="dna-entry-transition__skip"
        data-testid="dna-entry-skip"
        type="button"
        onClick={() => finishRef.current()}
      >
        跳过动画
      </button>
    </div>
  );
}
